import {
    scrollTop,
    dcrypt_url,
    encrypt_url
  } from "./Util_funs";
  
  export default {
    scrollTop,
    encrypt_url,
    dcrypt_url
  };

/**
 * Check if a workspace ID is included in the active outbound workspaces
 * @param {string} activeOutboundWorkspaces - Comma-separated workspace IDs (e.g., "41507,41508")
 * @param {string} workspaceId - The workspace ID to check
 * @returns {boolean} - True if the workspace ID is in the active list
 */
export const isWorkspaceActive = (activeOutboundWorkspaces, workspaceId) => {
    if (!activeOutboundWorkspaces || workspaceId === undefined || workspaceId === null || workspaceId === '') {
        return false;
    }
    
    // Split the comma-separated string and check if the workspace ID is included
    const workspaceIds = activeOutboundWorkspaces.split(',').map(id => id.trim());
    const idStr = String(workspaceId).trim();
    return workspaceIds.includes(idStr);
};

/**
 * Utility functions for handling backend validation errors
 */

/**
 * Converts backend validation errors to frontend format
 * @param {Object} backendErrors - Backend errors object with field names as keys and arrays of error messages
 * @returns {Object} - Frontend errors object with field names as keys and single error messages
 */
export const convertBackendErrors = (backendErrors) => {
    if (!backendErrors || typeof backendErrors !== 'object') {
        return {};
    }

    const frontendErrors = {};
    Object.keys(backendErrors).forEach(field => {
        // Backend returns array of errors, we take the first one
        frontendErrors[field] = Array.isArray(backendErrors[field]) 
            ? backendErrors[field][0] 
            : backendErrors[field];
    });

    return frontendErrors;
};

/**
 * Scrolls to the first error field and focuses it
 * @param {Object} errors - Object with field names as keys
 * @param {string} selector - CSS selector to find the element (default: '[name="${field}"]')
 */
export const scrollToFirstError = (errors, selector = '[name="${field}"]') => {
    if (!errors || typeof errors !== 'object') {
        return;
    }

    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) {
        return;
    }

    // Try multiple selectors to find the element
    const selectors = [
        `[name="${firstErrorField}"]`,
        `[data-field="${firstErrorField}"]`,
        `#${firstErrorField}`,
        `[id*="${firstErrorField}"]`
    ];

    let errorElement = null;
    for (const sel of selectors) {
        errorElement = document.querySelector(sel);
        if (errorElement) break;
    }

    if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
    }
};

/**
 * Handles API response and extracts validation errors
 * @param {Object} response - API response object
 * @returns {Object} - Object with status, errors, and message
 */
export const handleApiResponse = (response) => {
    if (response.status) {
        return { status: true };
    }

    // Handle validation errors from backend
    if (response.errors && typeof response.errors === 'object') {
        const backendErrors = convertBackendErrors(response.errors);
        return {
            status: false,
            errors: backendErrors,
            message: response.message || 'Validation failed'
        };
    }

    // Handle other types of errors
    return {
        status: false,
        message: response.message || 'An error occurred'
    };
};

/**
 * Handles API error catch block
 * @param {Error} error - Caught error object
 * @returns {Object} - Object with status and message
 */
export const handleApiError = (error) => {
    console.error('API Error:', error);
    
    // Check if it's a validation error response
    if (error.response?.data?.errors) {
        const backendErrors = convertBackendErrors(error.response.data.errors);
        return {
            status: false,
            errors: backendErrors,
            message: error.response.data.message || 'Validation failed'
        };
    }

    return {
        status: false,
        message: error.response?.data?.message || 'Network error occurred'
    };
};